import React, { useState } from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import * as Yup from "yup"

import { PlusIcon } from "@heroicons/react/24/solid"

import { gql, useClient, useMutation } from "hooks/urql"

import {
  AutocompleteField,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"
import ModalFooter from "components/ModalWithProvidedBody/Footer"

export type AddUserProps = {
  messageThreadId: string
  viewerType: "Tutor" | "Admin" | "Parent"
}

const AddUser: React.FC<AddUserProps> = ({ messageThreadId, viewerType }) => {
  const [open, setOpen] = useState(false)

  const client = useClient()
  const [, runMutation]: any[] = useMutation(addUserMutation, client)
  const formState = {
    messageThreadId,
    userId: "",
  }

  const ValidationSchema = Yup.object().shape({
    userId: Yup.string().required("Please select a user"),
  })

  const handleSubmit = (values, actions, closeModal) => {
    runMutation(values)
      .then(
        result => {
          const { failures } = result.data.messageThreadAddUser
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.info("User added")
            closeModal()
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <div className="mx-1">
      <div className="flex flex-col items-center space-y-1">
        <button
          className="m-0 flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border-0 bg-gray-300 p-0"
          onClick={() => setOpen(true)}
        >
          <PlusIcon className="m-0 h-12 w-12 p-0 text-white" />
        </button>
        <div>Add User</div>
      </div>
      <ModalWithProvidedBody
        modalTitle="Add User"
        modalClassName="bootstrap-modal"
        hideTrigger
        closeModal={() => setOpen(false)}
        isOpen={open}
      >
        {({ closeModal }) => (
          <Formik
            initialValues={formState}
            onSubmit={(values, actions) =>
              handleSubmit(values, actions, closeModal)
            }
            validationSchema={ValidationSchema}
          >
            <Form>
              <div className="modal-body">
                <AutocompleteField
                  api={
                    viewerType === "Admin"
                      ? "/api/admins/users/autocomplete_full_name"
                      : `/api/${viewerType.toLowerCase()}s/message_threads/autocomplete_messageable_users`
                  }
                  name="userId"
                  label="User Name"
                  placeholder="Search..."
                  valueAttribute={viewerType === "Admin" ? "id" : undefined}
                />
              </div>

              <ModalFooter cancelAction={closeModal} submitText="Add User" />
            </Form>
          </Formik>
        )}
      </ModalWithProvidedBody>
    </div>
  )
}

const addUserMutation = gql`
  mutation AddUser($messageThreadId: ID!, $userId: ID!) {
    messageThreadAddUser(messageThreadId: $messageThreadId, userId: $userId) {
      failures {
        message
      }
      messageThread {
        id
      }
    }
  }
`

export default AddUser
